import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { Autocomplete, CircularProgress, InputAdornment, debounce } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import MedicationIcon from "@mui/icons-material/Medication";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { routeConfigs } from "../../navigation/routeConfig";
import { UserContext } from "../../hooks/userContext";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import { NotificationType } from "../../constants/global";
import { useMutationCreateWantedItem } from "../../queries/wantedItem/hooks/useMutationCreateWantedItem";
import { pageTitleSx } from "../../constants/designTokens";

const INK = "#1E293B";
const INK_SOFT = "#475569";
const LINE_SOFT = "#F1F5F9";
const PRIMARY = "#2563EB";
const PRIMARY_DARK = "#1D4ED8";
const BODY_FONT = '"Source Sans 3", sans-serif';
const DISPLAY_FONT = '"Lexend", sans-serif';
const MONO_FONT = '"Spline Sans Mono", monospace';

const normalizeNdcValue = (value: string) => value.replace(/-/g, "");
const checkIfNdcSearch = (value: string): boolean => /^\d{3}/.test(value);

const capitalizeWords = (str: string) => {
  if (!str) return str;
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontFamily: BODY_FONT,
    fontSize: 14,
    bgcolor: "#fff",
    "& fieldset": { borderColor: "#E2E8F0" },
    "&:hover fieldset": { borderColor: "#475569" },
    "&.Mui-focused fieldset": { borderColor: PRIMARY, borderWidth: "1.5px" },
    "&.Mui-disabled": {
      bgcolor: "#F1F5F9",
    },
    "&.Mui-disabled fieldset": { borderColor: LINE_SOFT },
  },
  "& .MuiInputLabel-root": {
    fontFamily: BODY_FONT,
    color: INK_SOFT,
  },
};

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: 2,
      py: 1,
      borderBottom: `1px solid ${LINE_SOFT}`,
      "&:last-of-type": { borderBottom: "none" },
    }}
  >
    <Box
      sx={{
        fontSize: "11px",
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: INK_SOFT,
        pt: 0.25,
        flexShrink: 0,
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontFamily: label === "NDC" ? MONO_FONT : BODY_FONT,
        fontSize: label === "NDC" ? "13px" : "14px",
        fontWeight: 600,
        color: value ? INK : "#475569",
        textAlign: "right",
        wordBreak: "break-word",
      }}
    >
      {value || "—"}
    </Box>
  </Box>
);

const NewWishlistItem: React.FunctionComponent = () => {
  const { id }: any = useContext(UserContext);
  const { addToast } = useContext(ToasterContext);
  const navigate = useNavigate();

  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [fdaLoading, setFdaLoading] = useState<boolean>(false);
  const [fdaError, setFdaError] = useState<string>("");
  const [options, setOptions] = useState<any[]>([]);
  const [drugData, setDrugData] = useState<any>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);

  const medispanAbortControllerRef = useRef<AbortController | null>(null);

  const [createWantedItem, { loading }] = useMutationCreateWantedItem({
    onCompleted: () => {
      addToast({ type: NotificationType.Success, title: "Wishlist item added." });
      navigate(routeConfigs.wishlist.path);
    },
    onError: (error: any) => {
      addToast({ type: NotificationType.Error, title: error.message });
    },
  });

  const handleMedispanSearch = async (value: string) => {
    if (medispanAbortControllerRef.current) {
      medispanAbortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    medispanAbortControllerRef.current = abortController;

    try {
      setFdaLoading(true);
      const api = `${process.env.REACT_APP_API_URL}/medispan/get?searchTerm=${encodeURIComponent(value)}`;
      const response = await axios.get(api, { signal: abortController.signal });
      setFdaLoading(false);

      if (!Array.isArray(response.data)) {
        setFdaError("No Medispan results found for this NDC.");
        return;
      }

      const filteredData = response.data
        .filter((item: any) => item.product_ndc !== null || item.package_ndc !== null)
        .filter((item: any) => {
          const usingNdc = checkIfNdcSearch(value);
          if (usingNdc) {
            const searchNdc = normalizeNdcValue(value);
            const productNdc = item.product_ndc ? normalizeNdcValue(item.product_ndc) : null;
            const packageNdc = item.package_ndc ? normalizeNdcValue(item.package_ndc) : null;
            return (
              (productNdc && productNdc.startsWith(searchNdc)) ||
              (packageNdc && packageNdc.startsWith(searchNdc))
            );
          }

          return true;
        })
        .map((item: any) => ({
          ...item,
          brand_name: item.brand_name ? capitalizeWords(item.brand_name) : item.brand_name,
          generic_name: item.generic_name ? capitalizeWords(item.generic_name) : item.generic_name,
        }));

      if (filteredData.length === 0) {
        setFdaError("No Medispan results found for this NDC.");
      } else {
        setFdaError("");
        setOptions(filteredData);
      }
    } catch (error: any) {
      if (error?.code === "ERR_CANCELED") return;
      setFdaLoading(false);
      addToast({
        type: NotificationType.Error,
        title: error.response ? error.message : "Server side error",
      });
    }
  };

  const debouncedMedispanSearch = useCallback(
    debounce((value: string) => {
      handleMedispanSearch(value);
    }, 500),
    []
  );

  useEffect(() => {
    return () => {
      if (medispanAbortControllerRef.current) {
        medispanAbortControllerRef.current.abort();
      }
    };
  }, []);

  const handleSearch = (value: string) => {
    const trimmedValue = value.trimStart();
    setOptions([]);
    setFdaError("");
    setDrugData(null);

    if (trimmedValue.length >= 3) {
      debouncedMedispanSearch(trimmedValue);
    } else {
      setFdaLoading(false);
    }
  };

  const mappedValues = useMemo(() => {
    const ndcNumber = drugData?.package_ndc || drugData?.product_ndc || "";
    const drugName = drugData?.brand_name || drugData?.generic_name || "";
    const manufacturer = drugData?.labeler_name || "";
    const dosageForm = drugData?.dosage_form || "N/A";
    const strength = drugData?.active_ingredients?.[0]?.strength || "N/A";

    return { ndcNumber, drugName, manufacturer, dosageForm, strength };
  }, [drugData]);

  useEffect(() => {
    let isMounted = true;

    const resolveImage = async () => {
      if (!drugData) {
        setImageUrl(null);
        setImageLoading(false);
        return;
      }

      setImageLoading(true);

      const medispanImageUrl =
        drugData?.image_url || drugData?.imageUrl || drugData?.image?.url || null;
      if (medispanImageUrl) {
        if (isMounted) {
          setImageUrl(medispanImageUrl);
          setImageLoading(false);
        }
        return;
      }

      const ndcSource = drugData?.package_ndc || drugData?.product_ndc;
      if (!ndcSource) {
        if (isMounted) {
          setImageUrl(null);
          setImageLoading(false);
        }
        return;
      }

      try {
        const formattedNdc = ndcSource.replace(/-/g, "");
        const imageResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/drug-images?ndc=${formattedNdc}`
        );
        if (isMounted) {
          setImageUrl(imageResponse?.data?.imageUrl || null);
        }
      } catch (_error) {
        if (isMounted) {
          setImageUrl(null);
        }
      } finally {
        if (isMounted) {
          setImageLoading(false);
        }
      }
    };

    resolveImage();

    return () => {
      isMounted = false;
    };
  }, [drugData]);

  const handleSubmit = () => {
    if (!mappedValues.ndcNumber || !mappedValues.drugName || !mappedValues.manufacturer) {
      addToast({
        type: NotificationType.Warning,
        title: "Select an item from Medispan search first.",
      });
      return;
    }

    createWantedItem({
      variables: {
        data: {
          buyerUserId: +id,
          ndcNumber: mappedValues.ndcNumber,
          drugName: mappedValues.drugName,
          manufacturer: mappedValues.manufacturer,
          dosageForm: mappedValues.dosageForm,
          strength: mappedValues.strength,
        },
      },
    });
  };

  const canSubmit = Boolean(mappedValues.ndcNumber && mappedValues.drugName && mappedValues.manufacturer);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        bgcolor: "#F8FAFC",
        fontFamily: BODY_FONT,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          borderBottom: `1px solid ${LINE_SOFT}`,
          bgcolor: "#fff",
        }}
      >
        <IconBack onClick={() => navigate(routeConfigs.wishlist.path)} />
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h1" sx={pageTitleSx}>
            Add wishlist item
          </Typography>
          <Box sx={{ fontSize: 12.5, color: INK_SOFT }}>Search by drug name or NDC</Box>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2 }}>
        <Box
          sx={{
            maxWidth: 560,
            bgcolor: "#fff",
            borderRadius: "12px",
            border: `1px solid ${LINE_SOFT}`,
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 2, borderBottom: `1px solid ${LINE_SOFT}` }}>
            <Autocomplete
              options={options}
              filterOptions={(option: any) => option}
              inputValue={searchInputValue}
              getOptionLabel={(option: any) => {
                if (typeof option === "string") return option;
                if (!option) return "";
                return `${option?.brand_name || option?.generic_name} - ${option.package_ndc || option.product_ndc}`;
              }}
              isOptionEqualToValue={(option, value) => option?.package_ndc === value?.package_ndc}
              value={drugData}
              onChange={(_, newValue: any) => {
                setDrugData(newValue || null);
              }}
              onInputChange={(event: any, newValue: string) => {
                const normalizedValue = newValue.trimStart();
                setSearchInputValue(normalizedValue);
                if (event?.type === "change") {
                  handleSearch(normalizedValue);
                }
              }}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Drug name or NDC"
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ fontSize: 18, color: "#475569" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {fdaLoading ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {fdaError !== "" && (
              <Box sx={{ fontSize: "12px", mt: 1, color: "#A02121" }}>{fdaError}</Box>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              px: 2,
              py: 1.75,
              borderBottom: `1px solid ${LINE_SOFT}`,
              bgcolor: "#F1F5F9",
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "10px",
                flexShrink: 0,
                overflow: "hidden",
                border: `1px solid ${LINE_SOFT}`,
                bgcolor: imageUrl ? "#fff" : "#F1F5F9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {imageLoading ? (
                <CircularProgress size={18} sx={{ color: PRIMARY }} />
              ) : imageUrl ? (
                <Box
                  component="img"
                  src={imageUrl}
                  alt={mappedValues.drugName || "Drug"}
                  sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              ) : (
                <MedicationIcon sx={{ fontSize: 28, color: PRIMARY }} />
              )}
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "15px",
                  color: mappedValues.drugName ? INK : "#475569",
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {mappedValues.drugName || "Select a drug to preview"}
              </Typography>
              <Box
                sx={{
                  mt: 0.35,
                  fontFamily: MONO_FONT,
                  fontSize: "11.5px",
                  color: INK_SOFT,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {mappedValues.ndcNumber || "NDC will appear here"}
              </Box>
            </Box>
          </Box>

          <Box sx={{ px: 2, py: 0.25 }}>
            <DetailRow label="NDC" value={mappedValues.ndcNumber} />
            <DetailRow label="Manufacturer" value={mappedValues.manufacturer} />
            <DetailRow
              label="Dosage form"
              value={mappedValues.dosageForm === "N/A" && !drugData ? "" : mappedValues.dosageForm}
            />
            <DetailRow
              label="Strength"
              value={mappedValues.strength === "N/A" && !drugData ? "" : mappedValues.strength}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              px: 2,
              py: 1.5,
              borderTop: `1px solid ${LINE_SOFT}`,
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate(routeConfigs.wishlist.path)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                borderColor: "#E2E8F0",
                color: INK_SOFT,
                "&:hover": { borderColor: "#475569", bgcolor: "#F1F5F9" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSubmit}
              disabled={loading || !canSubmit}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                px: 2,
                boxShadow: "none",
                backgroundColor: PRIMARY,
                "&:hover": { backgroundColor: PRIMARY_DARK, boxShadow: "none" },
                "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#475569" },
              }}
            >
              Add to wishlist
              {loading && <CircularProgress sx={{ ml: 1 }} size={12} color="inherit" />}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const IconBack = ({ onClick }: { onClick: () => void }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    aria-label="Back to wishlist"
    sx={{
      width: 36,
      height: 36,
      borderRadius: "8px",
      border: `1px solid ${LINE_SOFT}`,
      bgcolor: "#fff",
      color: INK_SOFT,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      flexShrink: 0,
      p: 0,
      "&:hover": { bgcolor: "#F8FAFC", color: INK },
    }}
  >
    <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
  </Box>
);

export default NewWishlistItem;
